/**
 * @file Service for generating the routes and the menu.
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { ConfigurationsService } from './configurations.service';
import { ComponentRegistry } from './core/component-registry';
import { NotFoundComponent } from './core/not-found/not-found.component';
import { CanDeactivateGuard } from './core/guards/can-deactivate.guard';
import { NeedsUserResourceGuard } from './core/guards/needs-user-resource.guard';
/**
 * Name of Route data property to get, set the configuration reference.
 */
export var CONFIGURATION_ROUTE_DATA_PROPERTY = 'configKey';
/**
 * The name of the route parameter containing the key of the entity shown.
 */
export var ROUTE_PARAM_ID = 'id';
var RoutesService = /** @class */ (function () {
    /**
    * Creates an instance of RoutesService.
    * @param {ConfigurationsService} configurationsService
    * @memberof RoutesService
    */
    function RoutesService(configurationsService) {
        this.configurationsService = configurationsService;
        this.routes = [];
        this.menuItems = [];
        this.giveWarnings = true;
        /**
         * Optional guards for a route.
         */
        this.guards = {
            'needs-user-resource': NeedsUserResourceGuard
        };
    }
    /**
    * Methode is part of Bootflow in app.module.ts
    */
    RoutesService.prototype.bootInit = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _a;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = this.createRoutes;
                        return [4 /*yield*/, this.configurationsService.configApp()];
                    case 1:
                        _a.apply(this, [_b.sent()]);
                        this.createMenuItems();
                        return [2 /*return*/];
                }
            });
        });
    };
    RoutesService.prototype.createRoutes = function (appConfiguration) {
        var _this = this;
        this.routes.length = 0;
        appConfiguration.routes.forEach(function (configuration) {
            _this.buildRoutesTree(configuration);
        });
        // Keep track of the first route
        var firstRoute = this.routes[0];
        // Create redirect to first childs of parent routes
        var routesWithChildren = this.routes.filter(function (route) { return route.children && route.children.length > 0; });
        routesWithChildren.forEach(function (routeWithChildren) {
            _this.routes.unshift({
                path: routeWithChildren.path,
                redirectTo: '/' + routeWithChildren.path + '/' + routeWithChildren.children[0].path,
                pathMatch: 'full'
            });
        });
        // Redirect / to the first route
        this.routes.unshift({
            path: '',
            redirectTo: (firstRoute.children && firstRoute.children.length > 0) ? '/' + firstRoute.path + '/' + firstRoute.children[0].path : firstRoute.path,
            pathMatch: 'full'
        });
        // Add 404 page
        this.routes.push({
            path: '**',
            component: NotFoundComponent
        });
    };
    RoutesService.prototype.buildRoutesTree = function (configuration, parentRoute) {
        var _this = this;
        var path = this.getConfigPath(configuration);
        var routes = this.getConfigRoutes(configuration);
        // The base route
        var route = {
            path: path,
            children: [],
            data: {
                labelKey: configuration.labelKey
            }
        };
        if (configuration.component) {
            // Make sure we can find the component
            var component = ComponentRegistry.getComponent('route', configuration.component);
            // Update new route
            route = tslib_1.__assign({}, route, { 
                // pathMatch: 'full',
                canActivate: [], // AuthorizedRouteGuard TODO: not needed anymore?
                component: component, canDeactivate: [CanDeactivateGuard] });
            if (configuration.guards) {
                configuration.guards.forEach(function (key) {
                    var guard = _this.guards[key];
                    if (guard) {
                        route.canActivate.push(guard);
                        return;
                    }
                    throw new Error("Invalid guard \"" + key + "\" for route " + configuration.path + ".");
                });
            }
            // Add configuration to the route
            route.data[CONFIGURATION_ROUTE_DATA_PROPERTY] = configuration.configuration;
        }
        // Add global routes or add it as a child to an existing route
        if (!parentRoute) {
            this.routes.push(route);
        }
        else {
            parentRoute.children.push(route);
        }
        // Run through the subroutes for creating the routes tree
        routes.forEach(function (routeConfig) {
            _this.buildRoutesTree(routeConfig, route);
        });
    };
    RoutesService.prototype.getConfigPath = function (configuration) {
        // We prefer to use the path
        if (configuration.path) {
            if (configuration.path[0] === '/') {
                if (this.giveWarnings) {
                    console.warn('Stop using slashes in the paths (' + configuration.path + ')');
                }
                configuration.path = configuration.path.substring(1);
            }
            return configuration.path;
        }
        // If no path exists we try to use the name
        if (configuration.name) {
            return configuration.name.toLowerCase();
        }
        if (this.giveWarnings) {
            console.warn('Can not create path because both path and name are missing');
        }
        return '';
    };
    RoutesService.prototype.getConfigRoutes = function (configuration) {
        // Default we have routes on configuration
        if (configuration.routes) {
            return configuration.routes.slice();
        }
        // But sometimes (at the second level), we have routes in the configuration of the configuration
        if (configuration.configuration) {
            if (configuration.configuration.routes) {
                // if (this.giveWarnings) {
                //     console.warn('Nested configurations, is this really needed?');
                // }
                return configuration.configuration.routes.slice();
            }
        }
        return [];
    };
    RoutesService.prototype.createMenuItems = function () {
        var _this = this;
        this.menuItems.length = 0;
        this.routes.forEach(function (parentRoute) {
            // Ignore wildcard, redirects and empty paths's
            if (parentRoute.path === '**' || parentRoute.path === '' || parentRoute.redirectTo) {
                return;
            }
            var parentLink = (parentRoute.children[0]) ? '/' + parentRoute.path + '/' + parentRoute.children[0].path : '/' + parentRoute.path;
            var menuItem = {
                path: parentRoute.path,
                link: parentLink,
                labelKey: parentRoute.data['labelKey'],
                menuItems: []
            };
            _this.menuItems.push(menuItem);
            if (parentRoute.children) {
                parentRoute.children.forEach(function (childRoute) {
                    var childLink = '/' + parentRoute.path + '/' + childRoute.path;
                    var subMenuItem = {
                        path: childRoute.path,
                        link: childLink,
                        labelKey: childRoute.data['labelKey']
                    };
                    menuItem.menuItems.push(subMenuItem);
                });
            }
        });
    };
    /**
     * Navigates back to the parent url.
     * @static
     * @param {Router} router The router to navigate with.
     * @param {ActivatedRoute} fromRoute The child route.
     * @param {Params} params The params for the url.
     * @returns {Promise<boolean>} The promise of the navigation.
     * @memberOf RoutesService
     */
    RoutesService.navigateBack = function (router, fromRoute, params) {
        var stepBack = '';
        fromRoute.snapshot.url.forEach(function () {
            stepBack += '../';
        });
        return router.navigate([stepBack], { relativeTo: fromRoute, queryParams: params });
    };
    /**
     * Gets the id from router state when the state is a detail route otherwise returns null
     * @static
     * @param {RouterState} routerState The router state to check.
     * @returns {*} The id from the detail route otherwise null.
     * @memberOf RoutesService
     */
    RoutesService.getDetailId = function (routerState) {
        if (!routerState) {
            return null;
        }
        var route = routerState.snapshot.root;
        while (route.firstChild != null) {
            route = route.firstChild;
        }
        if (route.paramMap.keys.indexOf(ROUTE_PARAM_ID) === -1) {
            return null;
        }
        return route.params[ROUTE_PARAM_ID];
    };
    return RoutesService;
}());
export { RoutesService };
